import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { compareASC, breakpoints, useWindowWidth, } from '@awaze/design-system.utils.react';
import { Text } from '@awaze/design-system.components.text';
import { Button } from '@awaze/design-system.components.button';
import { ChevronLeft, PinMap } from '@awaze/design-system.components.icon';
import { buttonProtoProps, getInitialParent, selectLocation, getRHSHeaders, resolveDestinationListNavigation, } from './destination-list.utils';
import { StyledNavWrapperDiv, StyledBaseGridItem, StyledButton, StyledButtonBack, StyledDividerVert, StyledBaseGrid, StyledTextLoading, StyledDestinationListWrapper, } from './destination-list.styles';
import { DestinationListPopular } from './destination-list-popular';
import { useSearchFormContext } from '../search-form.context';
import { DestinationListPagination } from './destination-list-pagination';
/**
 *
 * Creates a traversable list of Destinations based on JSON data from individual FETCH-API requests
 *
 * Data source path logic:                  `[path][initialParent]_[locale].json`
 * Defaults to:                             `[path]countries_[locale].json` Unless property '@param {string|number} initialParent' is defined
 *
 * JSON Storybook data assets:
 * British Isles:                             //static/destination-data/legacy/en/*.*
 * Novasol:                                   //static/destination-data/novasol/en/*.*
 *
 * See test data file for JSON structure requirements:
 * //src/compositions/search-form/search-form-composition/destination-list.ts
 *
 * ---- Required properties:
 * @param {string} translations               Destination translations type <SearchFormLabelsDestination>
 * @param {function} destinationFetch         Expects object of @type {destinationListDataType}
 * ---- Optional properties:
 * @param {string|number} initialParent       Alternate prefix for initial JSON data source file name, if not 'countries'
 * @param {number} pageSize                   Will initiate paging where number of destinations supersedes this number
 * @param {number} pageSizePopular            Same as the above but for Popular-Destinations
 * @param {string} fetchDelay                 Milliseconds delay between fetching data Intended for heighted user experience, displaying 'Loading...' message
 * @param {boolean} sortAlphabetically        Sort JSON data ASC
 * @param {function} onDestinationSelected    Callback for opening Date-Selector overlay on Destination-List selection
 * @param {function} closeDestinationsList    Callback for closing the Destinations-List overlay
 * @returns {JSX.Element}                     Destinations-List JSX element
 */
export const DestinationList = ({ destinationFetch, initialParent = 'countries', pageSize = 20, pageSizePopular = 16, fetchDelay = 0, sortAlphabetically = false, translations, onDestinationSelected, closeDestinationsList, isSearchCallback, }) => {
    const { formHooks, locale, defaultConfig: { accommodationType }, } = useSearchFormContext();
    const componentMounted = useRef(true);
    const [destinationState, setDestinationState] = useState({
        destinations: undefined,
        navigationLevel: 0,
        legacyId: '',
        drupalId: '',
        hasCustomRootElement: ['countries', 'boats'].indexOf(initialParent?.toString()) === -1,
        initialAccommodationType: getInitialParent(initialParent, formHooks.getValues('accommodationType'), accommodationType?.options[0]?.options),
        pagesSize: pageSize,
        pageSlice: { from: 0, to: pageSize },
        loading: true,
        hasErrors: false,
    });
    const [rhsHeadersState, setRhsHeadersState] = useState(translations.rhsHeaders);
    const reset = (hasErrors = false) => {
        setDestinationState({
            ...destinationState,
            destinations: undefined,
            navigationLevel: 0,
            legacyId: '',
            drupalId: '',
            pageSlice: { from: 0, to: pageSize },
            loading: false,
            hasErrors,
        });
    };
    const getDestinations = (parentID, url = undefined) => {
        const { initialAccommodationType, hasCustomRootElement } = destinationState;
        parentID = parentID || initialAccommodationType;
        const accommodationType = formHooks.getValues('accommodationType');
        setTimeout(() => {
            destinationFetch(locale, parentID, accommodationType).then((jsonResponse) => {
                if (!jsonResponse ||
                    (jsonResponse?.status && jsonResponse?.status !== 200)) {
                    reset(true);
                    return;
                }
                // Sort locations ASC
                // Temporary solution. This should be done in the API
                sortAlphabetically &&
                    jsonResponse?.locations?.sort((a, b) => compareASC(a.name, b.name));
                // Add button 'All of [CURRENT-SELECTED-DESTINATION]' at position 0
                const destinations = {
                    ...jsonResponse,
                    // Replace response-data parentId with zero to force hasCustomRootElement to be root element: { parentId: 0 }
                    ...(hasCustomRootElement && parentID === initialParent
                        ? { parentId: '0' }
                        : {}),
                    ...((hasCustomRootElement ||
                        parentID !== initialAccommodationType) && {
                        locations: [
                            {
                                id: parentID,
                                name: `${translations.allOfPrefix} ${jsonResponse.name}`,
                                parentId: 0,
                                drupalId: formHooks.getValues('destinationId'), // get previous id
                                ...(url && { url }),
                            },
                            ...jsonResponse.locations,
                        ],
                    }),
                };
                // Popular destinations
                const getPopularLocations = () => {
                    const popularDest = [];
                    const hasSinglePopularLocations = jsonResponse?.popular?.locations;
                    if (hasSinglePopularLocations) {
                        popularDest.push(...hasSinglePopularLocations);
                    }
                    else {
                        jsonResponse?.locations
                            ?.filter((location) => location?.popular?.locations)
                            .forEach((popularLocation) => {
                            popularDest.push(...popularLocation.popular.locations);
                        });
                    }
                    return popularDest;
                };
                componentMounted.current &&
                    setDestinationState({
                        ...destinationState,
                        destinations,
                        popularDestinations: getPopularLocations(),
                        pageSlice: { from: 0, to: pageSize },
                        loading: false,
                    });
            });
        }, fetchDelay);
    };
    const reLoad = () => {
        setDestinationState({
            ...destinationState,
            pageSlice: { from: 0, to: pageSize },
            loading: true,
            hasErrors: false,
        });
        getDestinations();
    };
    useEffect(() => {
        if (componentMounted.current) {
            const rhsHeaders = getRHSHeaders(formHooks.getValues('accommodationType'), accommodationType?.options[0]?.options);
            rhsHeaders && setRhsHeadersState(rhsHeaders);
            getDestinations();
        }
        return () => {
            componentMounted.current = false;
        };
    }, []);
    const width = useWindowWidth();
    const isWideLayout = width >= breakpoints[0];
    const selectDestination = (location) => {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const { id, drupalId, legacyId, parentId, has_children, url } = location;
        let setDrupalId = '';
        if (location.countryId) {
            setDrupalId = location.countryId;
        }
        if (!location.countryId && parentId) {
            setDrupalId = drupalId;
        }
        selectLocation(location, formHooks);
        isSearchCallback?.(false);
        if (has_children) {
            setDestinationState({
                ...destinationState,
                navigationLevel: destinationState.navigationLevel++,
                legacyId,
                drupalId: setDrupalId,
                loading: true,
            });
            getDestinations(id, url);
        }
        else {
            closeDestinationsList?.();
            onDestinationSelected?.();
        }
    };
    const getPage = (newPageIndex) => {
        const { from, to } = destinationState.pageSlice;
        setDestinationState({
            ...destinationState,
            pageSlice: {
                from: from + pageSize * newPageIndex,
                to: to + pageSize * newPageIndex,
            },
        });
    };
    const navigateBack = (parentID) => {
        setDestinationState({
            ...destinationState,
            navigationLevel: destinationState.navigationLevel--,
            loading: true,
        });
        getDestinations(parentID);
    };
    if (destinationState.hasErrors) {
        const { error, noResultBtn } = translations.autocomplete;
        return (_jsxs("div", { children: [_jsx(Text, { sizes: 300, as: "p", mb: 16, children: error }), _jsx(Button, { variant: "outline", colourScheme: "brand-secondary", text: noResultBtn, onClick: reLoad })] }));
    }
    // Resolve initial parent for Places- or Drupal JSON -data Navigation-Back button
    const hasParent = resolveDestinationListNavigation(destinationState?.destinations?.parentId, destinationState?.hasCustomRootElement);
    const { navigationLevel, pagesSize } = destinationState;
    const destinationsLength = destinationState?.destinations?.locations?.length;
    const { from, to } = destinationState.pageSlice;
    return destinationState.loading ? (_jsx(StyledTextLoading, { sizes: 300, type: "mid", children: translations.autocomplete.loading })) : (_jsxs(StyledDestinationListWrapper, { "data-testid": "destination-list", children: [_jsxs(StyledNavWrapperDiv, { "data-testid": "destination-list-navigation", children: [hasParent !== null && (_jsxs(_Fragment, { children: [_jsx(StyledButtonBack, { "data-testid": "destination-list-back-button", ...buttonProtoProps, text: translations.backBtn, icon: _jsx(ChevronLeft, {}), iconPosition: "left", fullWidth: false, isFullWidth: false, onClick: () => navigateBack(hasParent !== '0' ? hasParent : undefined) }), _jsx(StyledDividerVert, { isVertical: true })] })), _jsx(Text, { sizes: 300, type: "mid", style: { paddingTop: '3px' }, children: rhsHeadersState
                            ? Object.values(rhsHeadersState)[navigationLevel]
                            : translations.accordionTitle }), isWideLayout && destinationsLength > pageSize && (_jsx(DestinationListPagination, { pageSize: pagesSize, from: from, to: to, destinationsLength: destinationsLength, getPage: getPage }))] }), _jsx(StyledBaseGrid, { flush: true, align: "stretch", children: destinationState?.destinations?.locations
                    ?.slice(from, isWideLayout ? to : destinationsLength)
                    .map((location) => (_createElement(StyledBaseGridItem, { ...(isWideLayout && {
                        columns: [12, 6, 3],
                    }), key: location.id },
                    _jsx(StyledButton, { ...buttonProtoProps, text: _jsx(Text, { sizes: 200, type: "mid", children: location.name }), variant: isWideLayout ? 'solid' : 'ghost', ...(!location.has_children && { icon: _jsx(PinMap, {}) }), onClick: () => {
                            selectDestination(location);
                        }, ...(!isWideLayout && { colourScheme: 'brand-primary' }) })))) }), destinationState?.popularDestinations?.length ? (_jsx(DestinationListPopular, { destinations: destinationState?.popularDestinations, pageSize: pageSizePopular, translations: translations, onDestinationSelected: onDestinationSelected, closeDestinationsList: closeDestinationsList })) : null] }));
};
//# sourceMappingURL=destination-list.js.map